"use client";
import { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { Calendar, MapPin, Users } from "lucide-react";

export default function ProjectsPage() {
  // useEffect(() => {
  //   AOS.init({ duration: 1000, once: true });
  // }, []);

  const projects = [
    {
      title: "Modern Residential Complex",
      description:
        "A 50-unit residential complex featuring modern amenities and sustainable design.",
      image:
        "https://i.pinimg.com/736x/1a/eb/b8/1aebb80ac83d1ad5ccda2bfdda9b1854.jpg",
      category: "Residential",
      location: "Downtown District",
      completedDate: "2023",
      client: "Private Developer",
      features: [
        "50 residential units",
        "Green building certified",
        "Underground parking",
        "Community center",
      ],
    },
    {
      title: "Corporate Office Renovation",
      description:
        "Complete renovation of a 10-story corporate office building with modern interiors.",
      image:
        "https://i.pinimg.com/736x/7f/7b/57/7f7b5776b2bcca0a14fd8f1f2968253f.jpg",
      category: "Commercial",
      location: "Business District",
      completedDate: "2023",
      client: "Tech Corporation",
      features: [
        "10 floors renovated",
        "Modern office spaces",
        "Energy-efficient systems",
        "Rooftop garden",
      ],
    },
    {
      title: "Luxury Villa Construction",
      description:
        "Custom-built luxury villa with premium finishes and smart home technology.",
      image:
        "https://i.pinimg.com/736x/fd/9f/1b/fd9f1b98bd765ffb9a2d62df2740f999.jpg",
      category: "Residential",
      location: "Hillside Estates",
      completedDate: "2022",
      client: "Private Client",
      features: [
        "5 bedrooms",
        "Smart home system",
        "Infinity pool",
        "Home theater",
      ],
    },
    {
      title: "Shopping Mall Development",
      description:
        "Development of a modern shopping mall with retail spaces and entertainment areas.",
      image:
        "https://i.pinimg.com/736x/56/d1/e3/56d1e3a914e44ae0be7a27e6e6709742.jpg",
      category: "Commercial",
      location: "City Center",
      completedDate: "2022",
      client: "Retail Group",
      features: [
        "200+ retail spaces",
        "Food court",
        "Cinema complex",
        "Parking for 1000 cars",
      ],
    },
    {
      title: "School Building Construction",
      description:
        "Construction of a modern elementary school with state-of-the-art facilities.",
      image:
        "https://i.pinimg.com/736x/5a/46/c2/5a46c2d059abd83f7f65cb06582e54ac.jpg",
      category: "Educational",
      location: "Suburban Area",
      completedDate: "2021",
      client: "City Council",
      features: [
        "30 classrooms",
        "Science laboratories",
        "Sports facilities",
        "Library and computer lab",
      ],
    },
    {
      title: "Hospital Wing Addition",
      description:
        "Addition of a new wing to existing hospital with specialized medical facilities.",
      image:
        "https://i.pinimg.com/736x/94/08/77/9408775a0f63aa79e6d7065846ef0e50.jpg",
      category: "Healthcare",
      location: "Medical District",
      completedDate: "2021",
      client: "Regional Hospital",
      features: [
        "50 patient rooms",
        "Operating theaters",
        "ICU facilities",
        "Emergency department",
      ],
    },
  ];

  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredProjects =
    selectedCategory === "All"
      ? projects
      : projects.filter((project) => project.category === selectedCategory);

  const categories = [
    "All",
    "Residential",
    "Commercial",
    "Educational",
    "Healthcare",
  ];

  return (
    <div className="min-h-screen py-20 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16" data-aos="fade-down">
          <div className="inline-block">
            <h1 className="text-5xl font-bold text-blue-700 mb-2">
              Our Projects
            </h1>
            <div className="h-1 w-40 bg-[#6FEF34] mx-auto mb-6 blink-underline"></div>
          </div>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore our portfolio of completed projects showcasing our expertise
            in construction and development
          </p>
        </div>

        {/* Category Filter */}
        <div
          className="flex flex-wrap justify-center gap-4 mb-12"
          data-aos="fade-up"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium border ${
                category === selectedCategory
                  ? "bg-blue-600 text-white border-blue-600"
                  : "border-gray-300 text-gray-700 hover:bg-blue-600 hover:text-white"
              } transition-all`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <div
              key={index}
              className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 bg-white"
              data-aos="zoom-in"
              data-aos-delay={index * 100}
            >
              <div className="relative">
                {/* <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover"
                /> */}
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-contain"
                />

                <span className="absolute top-4 left-4 bg-blue-600 text-white text-xs px-3 py-1 rounded-full">
                  {project.category}
                </span>
              </div>
              <div className="p-4">
                <h3 className="text-xl font-semibold text-gray-800">
                  {project.title}
                </h3>
                <p className="text-gray-600 text-sm mt-1">
                  {project.description}
                </p>

                <div className="mt-4 space-y-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 mr-2" />
                    {project.location}
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-2" />
                    Completed in {project.completedDate}
                  </div>
                  <div className="flex items-center">
                    <Users className="h-4 w-4 mr-2" />
                    {project.client}
                  </div>
                </div>

                <div className="mt-4">
                  <h4 className="text-sm font-semibold mb-2">Key Features:</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    {project.features.map((feature, i) => (
                      <li key={i} className="flex items-center">
                        <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">
              No projects found for the selected category.
            </p>
          </div>
        )}

        {/* Stats Section */}
        <section
          className="mt-20 bg-blue-50 rounded-lg p-12"
          data-aos="fade-up"
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">500+</div>
              <div className="text-gray-600">Projects Completed</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">15+</div>
              <div className="text-gray-600">Years Experience</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">1000+</div>
              <div className="text-gray-600">Happy Clients</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">50+</div>
              <div className="text-gray-600">Team Members</div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
